import React, { useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DataService } from "../../services/dataService";
import type { IDokumentItem } from "../../models/IDokumentItem";
import type { IProjektItem } from "../../models/IProjektItem";
import { DismissCircleFilled, EditRegular } from "@fluentui/react-icons";
import {
  Button,
  Checkbox,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Switch,
  TableCellLayout,
  Textarea,
  tokens,
  type TableColumnDefinition,
  type TableColumnId,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import Dokumente from "./dokumente";
import { useProjektplanStyles as useStyles } from "../../utils/styles";
import {
  formatDate,
  formatValue,
  getStudiengangsArtText,
  isSameDay,
  toDate,
} from "../../utils/format";
import type { IMeilensteinItem } from "../../models/IMeilensteinItem";
import { CheckmarkFilled } from "@fluentui/react-icons";
import type { IWorkflowItem } from "../../models/IWorkflowItem";

interface ProjektplanProps {
  canEdit?: boolean;
  projekt: IProjektItem;
  dokumente: IDokumentItem[];
  onProjektUpdated: () => Promise<void>;
  onDokumenteUpdated: () => Promise<void>;
}
type LoadingState = "anfordern" | "angefordert" | "genehmigt" | "abgelehnt";
type MeilensteinDateField = "sollDatum" | "istDatum";
type MeilensteinDrafts = Record<
  number,
  Partial<Pick<IMeilensteinItem, MeilensteinDateField | "nichtRelevant">>
>;

// Serialize as a local calendar date so the selected day doesn't shift with the UTC offset.
const toApiDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
};

const Projektplan: React.FC<ProjektplanProps> = ({
  canEdit = false,
  projekt,
  dokumente,
  onProjektUpdated,
  onDokumenteUpdated,
}) => {
  const panelTopRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [color, setColor] = useState("");

  const [anmerkung, setAnmerkung] = useState<string>(projekt.projektplan ?? "");
  const [bescheidRelevant, setBescheidRelevant] = useState<boolean>(
    projekt.bescheidRelevant ?? false,
  );
  const [meilensteine, setMeilensteine] = useState<IMeilensteinItem[]>([]);
  const [meilensteinDrafts, setMeilensteinDrafts] = useState<MeilensteinDrafts>(
    {},
  );
  const [anmerkungMeilensteinplan, setAnmerkungMeilensteinplan] =
    useState<string>(projekt.meilensteinplan ?? "");
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [updateBemerkung, setUpdateBemerkung] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [loadingState, setLoadingState] = React.useState<LoadingState>(
    projekt.workflowStatusProjektplan == "Nicht versendet"
      ? "anfordern"
      : projekt.workflowStatusProjektplan == "Angefordert"
        ? "angefordert"
        : projekt.workflowStatusProjektplan == "Genehmigt"
          ? "genehmigt"
          : "abgelehnt",
  );

  const scrollToTopOfPanel = () => {
    setTimeout(() => {
      if (panelTopRef.current) {
        // First, use scrollIntoView immediately
        panelTopRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });

        // Then find and scroll the scrollable parent to ensure we're at the top
        let element = panelTopRef.current.parentElement;
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY;
          const isScrollable = overflowY === "scroll" || overflowY === "auto";
          if (isScrollable && element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0;
            break;
          }
          element = element.parentElement;
        }
      }
    }, 150);
  };

  const buttonIcon =
    loadingState === "angefordert" ? (
      <Spinner size="tiny" />
    ) : loadingState === "genehmigt" ? (
      <CheckmarkFilled />
    ) : loadingState === "abgelehnt" ? (
      <DismissCircleFilled />
    ) : null;

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      const response = await dataService.getProjektMeilensteine(projekt.itemId);
      if (response) setMeilensteine(response);
    };
    fetchItems();
  }, [dataService, projekt.itemId]);

  const getMeilensteinDate = (
    item: IMeilensteinItem,
    field: MeilensteinDateField,
  ) => meilensteinDrafts[item.itemId]?.[field] ?? item[field];

  const renderTextCell = (item: IMeilensteinItem, value: React.ReactNode) => (
    <TableCellLayout>
      {!editMode && item.nichtRelevant ? <s>{value}</s> : value}
    </TableCellLayout>
  );

  const renderDateCell = (
    item: IMeilensteinItem,
    field: MeilensteinDateField,
    label: string,
  ) => {
    const value = getMeilensteinDate(item, field);
    if (!editMode || !canEdit) {
      return renderTextCell(item, value ? formatDate(value) : "");
    }
    return (
      <TableCellLayout
        style={{ width: "100%" }}
        main={{ style: { width: "100%" } }}
      >
        <DatePicker
          className={styles.input}
          aria-label={`${label} ${item.meilenstein ?? item.nummer}`}
          value={toDate(value) ?? null}
          disabled={isSaving}
          formatDate={formatDate}
          onSelectDate={(date) => {
            if (!date) return;
            setMeilensteinDrafts((drafts) => ({
              ...drafts,
              [item.itemId]: {
                ...drafts[item.itemId],
                [field]: toApiDate(date),
              },
            }));
          }}
        />
      </TableCellLayout>
    );
  };

  const getColumnStyle = (columnId: TableColumnId): React.CSSProperties =>
    columnId === "nummer" ? { flex: "0 0 60px", minWidth: "60px" } : {};

  const columns: TableColumnDefinition<IMeilensteinItem>[] = [
    createTableColumn<IMeilensteinItem>({
      columnId: "nummer",
      renderHeaderCell: () => "Nr.",
      compare: (a, b) => {
        const aNum = a.nummer ?? 0;
        const bNum = b.nummer ?? 0;
        return aNum - bNum;
      },
      renderCell: (item) => renderTextCell(item, item.nummer?.toString() ?? ""),
    }),
    createTableColumn<IMeilensteinItem>({
      columnId: "meilenstein",
      renderHeaderCell: () => "Meilenstein",
      compare: (a, b) =>
        (a.meilenstein ?? "").localeCompare(b.meilenstein ?? ""),
      renderCell: (item) => renderTextCell(item, item.meilenstein ?? ""),
    }),
    createTableColumn<IMeilensteinItem>({
      columnId: "durchfuehrung",
      renderHeaderCell: () => "Durchführung",
      compare: (a, b) =>
        (a.durchfuehrung ?? "").localeCompare(b.durchfuehrung ?? ""),
      renderCell: (item) => renderTextCell(item, item.durchfuehrung ?? ""),
    }),
    createTableColumn<IMeilensteinItem>({
      columnId: "sollDatum",
      renderHeaderCell: () => "Soll-Datum",
      compare: (a, b) => {
        const aTime = a.sollDatum ? new Date(a.sollDatum).getTime() : 0;
        const bTime = b.sollDatum ? new Date(b.sollDatum).getTime() : 0;
        return aTime - bTime;
      },
      renderCell: (item) => renderDateCell(item, "sollDatum", "Soll-Datum"),
    }),
    createTableColumn<IMeilensteinItem>({
      columnId: "istDatum",
      renderHeaderCell: () => "Ist-Datum",
      compare: (a, b) => {
        const aTime = a.istDatum ? new Date(a.istDatum).getTime() : 0;
        const bTime = b.istDatum ? new Date(b.istDatum).getTime() : 0;
        return aTime - bTime;
      },
      renderCell: (item) => renderDateCell(item, "istDatum", "Ist-Datum"),
    }),
  ];

  if (editMode && canEdit) {
    columns.push(
      createTableColumn<IMeilensteinItem>({
        columnId: "nichtRelevant",
        renderHeaderCell: () => "Nicht relevant",
        renderCell: (item) => (
          <TableCellLayout>
            <Checkbox
              aria-label={`Nicht relevant ${item.meilenstein ?? item.nummer}`}
              checked={
                meilensteinDrafts[item.itemId]?.nichtRelevant ??
                item.nichtRelevant
              }
              disabled={isSaving}
              onChange={(_, data) => {
                setMeilensteinDrafts((drafts) => ({
                  ...drafts,
                  [item.itemId]: {
                    ...drafts[item.itemId],
                    nichtRelevant: data.checked === true,
                  },
                }));
              }}
            />
          </TableCellLayout>
        ),
      }),
    );
  }

  const saveProjekt = async () => {
    if (isSaving) return;
    setErrorMessage("");
    if (!canEdit || !dataService) {
      setErrorMessage("Die Daten können derzeit nicht gespeichert werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    setIsSaving(true);
    try {
      const result = await dataService.addUpdateProjekt({
        ...projekt,
        projektplan: anmerkung,
        bescheidRelevant: bescheidRelevant,
        meilensteinplan: anmerkungMeilensteinplan,
        updateBemerkung: updateBemerkung,
        kategorie: "Projektplan",
      });
      if (result === false) {
        throw new Error("Project update was not successful.");
      }

      const changedMeilensteine = meilensteine.filter((item) => {
        const draft = meilensteinDrafts[item.itemId];
        return (
          draft &&
          ((draft.sollDatum !== undefined &&
            !isSameDay(draft.sollDatum, item.sollDatum)) ||
            (draft.istDatum !== undefined &&
              !isSameDay(draft.istDatum, item.istDatum)) ||
            (draft.nichtRelevant !== undefined &&
              draft.nichtRelevant !== item.nichtRelevant))
        );
      });

      for (const item of changedMeilensteine) {
        const updated = await dataService.updateMeilenstein({
          ...item,
          ...meilensteinDrafts[item.itemId],
        });
        if (updated === false) {
          throw new Error(
            `Meilenstein ${item.itemId} update was not successful.`,
          );
        }
      }

      if (changedMeilensteine.length > 0) {
        const response = await dataService.getProjektMeilensteine(
          projekt.itemId,
        );
        setMeilensteine(response);
      }
      setMeilensteinDrafts({});

      await onProjektUpdated();
      setUpdateBemerkung("");
      setErrorMessage("Daten erfolgreich gespeichert");
      setColor("green");
      scrollToTopOfPanel();
      setTimeout(() => {
        setErrorMessage("");
        setEditMode(false);
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to save project or competencies:", error);
      setErrorMessage(
        "Fehler beim Speichern der Projektdaten oder Meilensteine. Bereits gespeicherte Änderungen bleiben erhalten. Bitte erneut versuchen.",
      );
      setColor("red");
      scrollToTopOfPanel();
      setIsSaving(false);
    }
  };

  const _saveClicked = () => {
    setIsDialogOpen(true);
  };

  const _startWorkflow = async () => {
    if (isSaving) return;
    setErrorMessage("");
    if (!canEdit || !dataService) {
      setErrorMessage("Die Daten können derzeit nicht gespeichert werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    setIsSaving(true);
    try {
      const item: IWorkflowItem = {
        projektId: projekt.itemId,
        source: "Projektplan",
        genehmigt: false,
        abgelehnt: false,
        id: 0,
        zugewiesen: "",
        titel: "",
        link: "",
        beschreibung: "",
        taskGuid: "",
        createdBy: "",
        created: new Date(),
        kommentar: "",
      };
      const response = await dataService.startWorkflow(item);
      if (response === false) {
        throw new Error(
          `Workflow start was not successful for project ${item.projektId}.`,
        );
      }
      await onProjektUpdated();
      setErrorMessage("Workflow erfolgreich gestartet");
      setColor("green");
      scrollToTopOfPanel();
      setTimeout(() => {
        setErrorMessage("");
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to start workflow:", error);
      setErrorMessage(
        "Fehler beim Starten des Workflows. Bitte erneut versuchen.",
      );
      setColor("red");
      setIsSaving(false);
      scrollToTopOfPanel();
    }
  };

  const renderReadMode = () => (
    <>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Projektplan</h3>
            {canEdit && (
              <Button
                appearance="secondary"
                icon={<EditRegular />}
                onClick={() => {
                  setAnmerkung(projekt.projektplan ?? "");
                  setAnmerkungMeilensteinplan(projekt.meilensteinplan ?? "");
                  setMeilensteinDrafts({});
                  setEditMode(true);
                }}
              >
                Bearbeitungsmodus starten
              </Button>
            )}
          </div>
          <div>
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "bolder",
                color:
                  projekt.workflowStatusProjektplan === "Abgelehnt"
                    ? "#b33a34"
                    : projekt.workflowStatusProjektplan === "Genehmigt"
                      ? "#408a3e"
                      : projekt.workflowStatusProjektplan === "Angefordert"
                        ? "#d69d60"
                        : "#608bd6",
              }}
            >
              {projekt.workflowProjektplan}
            </div>
            <div className={styles.projectSummary}>
              <div className={styles.projectSummaryItem}>
                <div className={styles.projectSummaryLabel}>Studiengang</div>
                <div className={styles.projectSummaryValue}>
                  {formatValue(projekt.studiengang)}
                </div>
              </div>
              <div className={styles.projectSummaryItem}>
                <div className={styles.projectSummaryLabel}>
                  Studiengangsleitung
                </div>
                <div className={styles.projectSummaryValue}>
                  {formatValue(projekt.studiengangsleitungName)}
                </div>
              </div>
              <div className={styles.projectSummaryItem}>
                <div className={styles.projectSummaryLabel}>
                  Projektmanagement
                </div>
                <div className={styles.projectSummaryValue}>
                  {formatValue(projekt.entwicklerName)}
                </div>
              </div>
              <div className={styles.projectSummaryItem}>
                <div className={styles.projectSummaryLabel}>
                  Studienplan/Version
                </div>
                <div className={styles.projectSummaryValue}>Version 1.1</div>
              </div>
              <div className={styles.projectSummaryItem}>
                <div className={styles.projectSummaryLabel}>Start</div>
                <div className={styles.projectSummaryValue}>
                  {formatDate(projekt.studiengangsstart)}
                </div>
              </div>
              <div className={styles.projectSummaryItem}>
                <div className={styles.projectSummaryLabel}>
                  Geplante Entwicklung
                </div>
                <div className={styles.projectSummaryValue}>
                  {formatValue(projekt.typ)}
                </div>
              </div>
            </div>
            <div className={styles.gridContainer}>
              <div className={styles.label} style={{ gridColumn: "1" }}>
                Projektname:
              </div>
              <div className={styles.valueSingle}>
                {formatValue(projekt.bezeichnung)}
              </div>
              <div className={styles.valueWeiter}>
                {projekt.statusId === 2
                  ? "Projekt wird nicht weiterverfolgt"
                  : ""}
              </div>
              <div className={styles.label}>Studiengangsbezeichnung:</div>
              <div className={styles.value}>
                {formatValue(projekt.studiengang)}
              </div>
              <div className={styles.label}>Studiengangsart:</div>
              <div className={styles.value}>
                {getStudiengangsArtText(projekt.studiengangsArt)}
              </div>

              <div className={styles.label}>Unterrichtssprache:</div>
              <div className={styles.value}>
                {formatValue(projekt.unterrichtssprache)}
              </div>

              <div className={styles.label}>Institution:</div>
              <div className={styles.value}>
                {formatValue(projekt.institution)}
              </div>

              <div className={styles.label}>
                Akademischer Grad (inkl. Abkürzung):
              </div>
              <div className={styles.value}>
                {formatValue(projekt.akadGrad)}
              </div>

              <div className={styles.label}>Studienfach:</div>
              <div className={styles.value}>
                {formatValue(projekt.studienfach)}
              </div>

              <div className={styles.label}>Studiengangsleitung:</div>
              <div className={styles.value}>
                {formatValue(projekt.studiengangsleitungName)}
              </div>

              <div className={styles.label}>Studiengangsstart:</div>
              <div className={styles.value}>
                {formatDate(projekt.studiengangsstart)}
              </div>

              <div className={styles.label}>Anzahl der Studienplätze:</div>
              <div className={styles.value}>
                {formatValue(projekt.studienplaetze)}
              </div>

              <div className={styles.label}>Organisationsstruktur:</div>
              <div className={styles.value}>
                {formatValue(projekt.organisationsstruktur)}
              </div>

              <div className={styles.label}>Programmstandort:</div>
              <div className={styles.value}>
                {formatValue(projekt.standort)}
              </div>

              <div className={styles.label}>Gesamtumfang in ECTS-Punkten:</div>
              <div className={styles.value}>{formatValue(projekt.ects)}</div>

              <div className={styles.label}>Qualifikationsniveau:</div>
              <div className={styles.value}>
                {formatValue(projekt.qualifikationsniveau)}
              </div>

              <div className={styles.label}>Studiendauer in Semestern:</div>
              <div className={styles.value}>
                {formatValue(projekt.studiendauer)}
              </div>
            </div>
            <div className={styles.gridSubContainer}>
              <div className={styles.label} style={{ gridColumn: "1" }}>
                Ergänzende Anmerkung:
              </div>
              <div className={styles.valueSingle}>
                {formatValue(projekt.projektplan)}
              </div>
              <div className={styles.valueWeiter}>
                <Switch
                  label={
                    bescheidRelevant
                      ? "Bescheidrelevant"
                      : "Nicht bescheidrelevant"
                  }
                  aria-checked={bescheidRelevant}
                  checked={bescheidRelevant}
                  disabled={true}
                />
              </div>
            </div>
          </div>
        </section>
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Meilensteinplan</h3>
          </div>
          <div className={styles.grid}>
            <DataGrid
              items={meilensteine}
              columns={columns}
              sortable
              size="medium"
              aria-label="Meilensteinplan"
              focusMode="composite"
              style={{ width: "100%" }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell, columnId }) => (
                    <DataGridHeaderCell
                      className="dataGridHeaderCell"
                      style={{
                        ...getColumnStyle(columnId),
                        backgroundColor: tokens.colorNeutralBackground3,
                        color: tokens.colorNeutralForeground1,
                      }}
                    >
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<IMeilensteinItem>>
                {({ item, rowId }) => (
                  <DataGridRow
                    key={rowId}
                    style={{
                      backgroundColor:
                        Number(rowId) % 2 === 1
                          ? tokens.colorNeutralBackground2
                          : tokens.colorNeutralBackground1,
                      color: tokens.colorNeutralForeground1,
                    }}
                  >
                    {(column) => (
                      <DataGridCell
                        className="dataGridCell"
                        style={{
                          ...getColumnStyle(column.columnId),
                          color: tokens.colorNeutralForeground1,
                        }}
                      >
                        {column.renderCell(item)}
                      </DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          </div>
          <div className={styles.gridSubContainer}>
            <div className={styles.label} style={{ gridColumn: "1" }}>
              Ergänzende Anmerkung:
            </div>
            <div className={styles.valueSingle}>
              {formatValue(projekt.meilensteinplan)}
            </div>
          </div>
        </section>
        <Dokumente projektId={projekt.itemId} dokumente={dokumente} />
      </div>
    </>
  );

  const renderEditMode = () => (
    <>
      <div ref={panelTopRef}>
        {errorMessage && (
          <div
            style={{
              fontSize: "16px",
              marginBottom: "30px",
              maxWidth: "500px",
              color: color,
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Projektplan</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              {canEdit && (
                <Button
                  appearance="primary"
                  disabled={isSaving}
                  onClick={() => {
                    _saveClicked();
                  }}
                >
                  Speichern
                </Button>
              )}
              <Button
                appearance="secondary"
                onClick={() => {
                  setErrorMessage("");
                  setUpdateBemerkung("");
                  setMeilensteinDrafts({});
                  setEditMode(false);
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
          <div>
            <div className={styles.gridContainer}>
              {canEdit && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "8px",
                    paddingBottom: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "bolder",
                      fontSize: "14px",
                      color:
                        projekt.workflowStatusProjektplan === "Abgelehnt"
                          ? "#b33a34"
                          : projekt.workflowStatusProjektplan === "Genehmigt"
                            ? "#408a3e"
                            : projekt.workflowStatusProjektplan ===
                                "Angefordert"
                              ? "#d69d60"
                              : "#608bd6",
                    }}
                  >
                    {projekt.workflowProjektplan}
                  </span>

                  <Button
                    appearance="primary"
                    disabled={isSaving || loadingState !== "anfordern"}
                    onClick={() => {
                      _startWorkflow();
                      setLoadingState("angefordert");
                    }}
                    style={{
                      backgroundColor:
                        loadingState === "abgelehnt" ? "firebrick" : undefined,
                    }}
                    icon={buttonIcon}
                  >
                    Freigabe {loadingState}
                  </Button>
                </div>
              )}
              <div className={styles.projectSummary}>
                <div className={styles.projectSummaryItem}>
                  <div className={styles.projectSummaryLabel}>Studiengang</div>
                  <div className={styles.projectSummaryValue}>
                    {formatValue(projekt.studiengang)}
                  </div>
                </div>
                <div className={styles.projectSummaryItem}>
                  <div className={styles.projectSummaryLabel}>
                    Studiengangsleitung
                  </div>
                  <div className={styles.projectSummaryValue}>
                    {formatValue(projekt.studiengangsleitungName)}
                  </div>
                </div>
                <div className={styles.projectSummaryItem}>
                  <div className={styles.projectSummaryLabel}>
                    Projektmanagement
                  </div>
                  <div className={styles.projectSummaryValue}>
                    {formatValue(projekt.entwicklerName)}
                  </div>
                </div>
                <div className={styles.projectSummaryItem}>
                  <div className={styles.projectSummaryLabel}>
                    Studienplan/Version
                  </div>
                  <div className={styles.projectSummaryValue}>Version 1.1</div>
                </div>
                <div className={styles.projectSummaryItem}>
                  <div className={styles.projectSummaryLabel}>Start</div>
                  <div className={styles.projectSummaryValue}>
                    {formatDate(projekt.studiengangsstart)}
                  </div>
                </div>
                <div className={styles.projectSummaryItem}>
                  <div className={styles.projectSummaryLabel}>
                    Geplante Entwicklung
                  </div>
                  <div className={styles.projectSummaryValue}>
                    {formatValue(projekt.typ)}
                  </div>
                </div>
              </div>
              <div className={styles.label} style={{ gridColumn: "1" }}>
                Projektname:
              </div>
              <div className={styles.valueSingle}>
                {formatValue(projekt.bezeichnung)}
              </div>
              <div className={styles.valueWeiter}>
                {projekt.statusId === 2
                  ? "Projekt wird nicht weiterverfolgt"
                  : ""}
              </div>
              <div className={styles.label}>Studiengangsbezeichnung:</div>
              <div className={styles.value}>
                {formatValue(projekt.studiengang)}
              </div>
              <div className={styles.label}>Studiengangsart:</div>
              <div className={styles.value}>
                {getStudiengangsArtText(projekt.studiengangsArt)}
              </div>

              <div className={styles.label}>Unterrichtssprache:</div>
              <div className={styles.value}>
                {formatValue(projekt.unterrichtssprache)}
              </div>

              <div className={styles.label}>Institution:</div>
              <div className={styles.value}>
                {formatValue(projekt.institution)}
              </div>

              <div className={styles.label}>
                Akademischer Grad (inkl. Abkürzung):
              </div>
              <div className={styles.value}>
                {formatValue(projekt.akadGrad)}
              </div>

              <div className={styles.label}>Studienfach:</div>
              <div className={styles.value}>
                {formatValue(projekt.studienfach)}
              </div>

              <div className={styles.label}>Studiengangsleitung:</div>
              <div className={styles.value}>
                {formatValue(projekt.studiengangsleitungName)}
              </div>

              <div className={styles.label}>Studiengangsstart:</div>
              <div className={styles.value}>
                {formatDate(projekt.studiengangsstart)}
              </div>

              <div className={styles.label}>Anzahl der Studienplätze:</div>
              <div className={styles.value}>
                {formatValue(projekt.studienplaetze)}
              </div>

              <div className={styles.label}>Organisationsstruktur:</div>
              <div className={styles.value}>
                {formatValue(projekt.organisationsstruktur)}
              </div>

              <div className={styles.label}>Programmstandort:</div>
              <div className={styles.value}>
                {formatValue(projekt.standort)}
              </div>

              <div className={styles.label}>Gesamtumfang in ECTS-Punkten:</div>
              <div className={styles.value}>{formatValue(projekt.ects)}</div>

              <div className={styles.label}>Qualifikationsniveau:</div>
              <div className={styles.value}>
                {formatValue(projekt.qualifikationsniveau)}
              </div>

              <div className={styles.label}>Studiendauer in Semestern:</div>
              <div className={styles.value}>
                {formatValue(projekt.studiendauer)}
              </div>
            </div>
            <div className={styles.gridSubContainer}>
              <div className={styles.label} style={{ gridColumn: "1" }}>
                Ergänzende Anmerkung:
              </div>
              <div className={styles.valueSingle}>
                <Textarea
                  className={styles.input}
                  id="anmerkung"
                  value={anmerkung}
                  disabled={isSaving}
                  autoFocus
                  rows={2}
                  onChange={(e) => setAnmerkung(e.target.value)}
                />
              </div>
              <div className={styles.valueWeiter}>
                <Switch
                  label={
                    bescheidRelevant
                      ? "Bescheidrelevant"
                      : "Nicht bescheidrelevant"
                  }
                  aria-checked={bescheidRelevant}
                  checked={bescheidRelevant}
                  onChange={(_, data) =>
                    setBescheidRelevant(data.checked ? true : false)
                  }
                />
              </div>
            </div>
          </div>
        </section>
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Meilensteinplan</h3>
          </div>
          <div className={styles.grid}>
            <DataGrid
              items={meilensteine}
              columns={columns}
              sortable
              size="medium"
              aria-label="Meilensteinplan"
              focusMode="composite"
              style={{ width: "100%" }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell, columnId }) => (
                    <DataGridHeaderCell
                      className="dataGridHeaderCell"
                      style={{
                        ...getColumnStyle(columnId),
                        backgroundColor: tokens.colorNeutralBackground3,
                        color: tokens.colorNeutralForeground1,
                      }}
                    >
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<IMeilensteinItem>>
                {({ item, rowId }) => (
                  <DataGridRow
                    key={rowId}
                    style={{
                      backgroundColor:
                        Number(rowId) % 2 === 1
                          ? tokens.colorNeutralBackground2
                          : tokens.colorNeutralBackground1,
                      color: tokens.colorNeutralForeground1,
                    }}
                  >
                    {(column) => (
                      <DataGridCell
                        className="dataGridCell"
                        style={{
                          ...getColumnStyle(column.columnId),
                          color: tokens.colorNeutralForeground1,
                        }}
                      >
                        {column.renderCell(item)}
                      </DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          </div>
          <div className={styles.gridSubContainer}>
            <div className={styles.label} style={{ gridColumn: "1" }}>
              Ergänzende Anmerkung:
            </div>
            <div className={styles.valueSingle}>
              <Textarea
                className={styles.input}
                id="anmerkungMeilensteinplan"
                value={anmerkungMeilensteinplan}
                disabled={isSaving}
                rows={2}
                onChange={(e) => setAnmerkungMeilensteinplan(e.target.value)}
              />
            </div>
          </div>
        </section>
        <Dokumente
          projektId={projekt.itemId}
          dokumente={dokumente}
          editable={canEdit}
          uploadNotice={
            <>
              <strong>Entwicklungsteammitglieder:</strong> Bitte die aktuelle
              ET-Liste im File Container hochladen.
            </>
          }
          onDokumenteUpdated={onDokumenteUpdated}
        />
      </div>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(_e, data) => setIsDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogTitle style={{ marginBottom: "10px" }}>
            Änderungsbegründung
          </DialogTitle>
          <DialogContent>
            <div className={styles.label}>Begründung der Änderung:</div>
            <div className={styles.value}>
              <Textarea
                className={styles.input}
                id="bemerkung"
                value={updateBemerkung}
                autoFocus
                rows={3}
                onChange={(e) => setUpdateBemerkung(e.target.value)}
              />
            </div>
          </DialogContent>
          <DialogActions style={{ marginTop: "10px" }}>
            <Button
              appearance="primary"
              disabled={!updateBemerkung || isSaving}
              onClick={() => {
                setIsDialogOpen(false);
                void saveProjekt();
              }}
            >
              Speichern
            </Button>
            <Button
              appearance="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Abbrechen
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );

  return editMode ? renderEditMode() : renderReadMode();
};

export default Projektplan;
